import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import {
  People as PeopleIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import ErrorIcon from "@mui/icons-material/Error";
import CountUp from "react-countup";
import axios from "../../config/axios";

function Dashboard() {
  const navigate = useNavigate();
  const [adminCount, setAdminCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get("/count");
        setAdminCount(response.data.adminCount);
        setProductCount(response.data.productCount);
      } catch (err) {
        console.error("Error fetching counts:", err);
        setError(err.response?.data?.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };
    fetchCounts();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <CircularProgress style={{ color: "#FFFFFF" }} />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "20px",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          background: "#FFFFFF",
          borderRadius: "12px",
          padding: "30px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <h1
          style={{
            color: "#263238",
            fontSize: "28px",
            fontWeight: 600,
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          Dashboard
        </h1>

        {error && (
          <div
            style={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            <ErrorIcon style={{ marginRight: "8px" }} />
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
          }}
        >
          <div
            style={{
              padding: "20px",
              background: "#F5F5F5",
              borderRadius: "12px",
              textAlign: "center",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <PeopleIcon
              style={{
                fontSize: "40px",
                color: "#26A69A",
                marginBottom: "10px",
              }}
            />
            <h3 style={{ color: "#263238", fontSize: "18px", fontWeight: 500 }}>
              Total Admin
            </h3>
            <h2 style={{ color: "#26A69A", fontSize: "32px", fontWeight: 600 }}>
              <CountUp end={adminCount} duration={2} />
            </h2>
          </div>
          <div
            style={{
              padding: "20px",
              background: "#F5F5F5",
              borderRadius: "12px",
              textAlign: "center",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <InventoryIcon
              style={{
                fontSize: "40px",
                color: "#FF6F61",
                marginBottom: "10px",
              }}
            />
            <h3 style={{ color: "#263238", fontSize: "18px", fontWeight: 500 }}>
              Total Products
            </h3>
            <h2 style={{ color: "#FF6F61", fontSize: "32px", fontWeight: 600 }}>
              <CountUp end={productCount} duration={2} />
            </h2>
          </div>
        </div>
        <div
          style={{
            padding: "20px",
            background: "#F5F5F5",
            borderRadius: "12px",
            textAlign: "center",
            transition: "transform 0.3s ease-in-out",
          }}
        >
          <h3
            style={{
              color: "#263238",
              fontSize: "20px",
              fontWeight: 600,
              marginBottom: "10px",
            }}
          >
            Welcome to Admin Panel
          </h3>
          <p style={{ color: "#263238", marginBottom: "20px" }}>
            Manage your users and products efficiently from this central
            dashboard. Use the navigation menu or the buttons below to get
            started.
          </p>
          <div
            style={{ display: "flex", justifyContent: "center", gap: "20px" }}
          >
            <Button
              onClick={() => navigate("/admin")}
              style={{
                background: "#26A69A",
                color: "#FFFFFF",
                padding: "10px 20px",
                borderRadius: "8px",
                fontWeight: 500,
                textTransform: "none",
                transition: "all 0.3s",
              }}
              sx={{
                "&:hover": { background: "#009688", transform: "scale(1.02)" },
              }}
            >
              Manage Admin
            </Button>
            <Button
              onClick={() => navigate("/products")}
              style={{
                background: "#FF6F61",
                color: "#FFFFFF",
                padding: "10px 20px",
                borderRadius: "8px",
                fontWeight: 500,
                textTransform: "none",
                transition: "all 0.3s",
              }}
              sx={{
                "&:hover": { background: "#F4511E", transform: "scale(1.02)" },
              }}
            >
              Manage Products
            </Button>
          </div>
        </div>

        <style jsx global>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideIn {
            from {
              transform: translateX(-20px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

export default Dashboard;
